<script setup lang='ts'>

import { ref, onBeforeMount, watch, computed } from 'vue'
import { useRoute } from 'vue-router'
import { clickListMenuCategory } from '~/composables/helpersComposable'
const route = useRoute()
const { layoutConfig, layoutState, setActiveMenuItem, onMenuToggle } = useLayout()
const activeMenuComposable = useActiveMenu()

const props = defineProps({
	item: {
		type: Object,
		default: () => ({})
	},
	index: {
		type: Number,
		default: 0
	},
	root: {
		type: Boolean,
		default: true
	},
	parentItemKey: {
		type: String,
		default: null
	},
	compact: {
		type: Boolean,
		default: false
	}
})

// Testo tooltip per menu compatto
const tooltipText = computed(() => {
	return props.compact && props.item.label ? props.item.label : null
})

// Separare espansione da selezione
const isExpandedMenu = ref(false) // per controllare l'espansione del sottomenu
const itemKey = ref<string | null>(null)

onBeforeMount(() => {
	itemKey.value = props.parentItemKey ? props.parentItemKey + '-' + props.index : String(props.index)
	const activeItem = layoutConfig.activeMenuItem.value

	if (activeItem && typeof activeItem === 'string' && itemKey.value) {
		isExpandedMenu.value = activeItem === itemKey.value || activeItem.startsWith(itemKey.value + '-')
	}
})

watch(
	() => layoutConfig.activeMenuItem.value,
	(newVal) => {
		if (newVal && typeof newVal === 'string' && itemKey.value) {
			isExpandedMenu.value = newVal === itemKey.value || newVal.startsWith(itemKey.value + '-')
		}
	}
)

// Computed per verificare se questo specifico item è attivo
const isThisItemActive = computed(() => {
	const itemTo = props.item.to
	const hasItems = !!props.item.items
	const itemIdMenuRif = props.item.idmenuRif

	// Se è un link esterno, non è mai "attivo" nel menu
	const isExternalLink = itemTo && (itemTo.startsWith('http://') || itemTo.startsWith('https://'))
	if (isExternalLink) {
		return false
	}

	// Funzione helper per normalizzare i path (rimuove trailing slash)
	const normalizePath = (path: string) => path.endsWith('/') && path !== '/' ? path.slice(0, -1) : path

	// 1. Se l'item ha un idmenuRif (submenu CCNL), verifica SOLO tramite categoria
	if (itemIdMenuRif) {
		return activeMenuComposable.isCategoryActive(itemIdMenuRif)
	}

	// 2. Se l'item ha un path, verifica il match con la route (normalizzato)
	if (itemTo) {
		const normalizedItemTo = normalizePath(itemTo)
		const normalizedRoutePath = normalizePath(route.path)
		const normalizedActiveMenuPath = normalizePath(activeMenuComposable.activeMenuPath.value)

		// Match esatto con route.path
		if (normalizedRoutePath === normalizedItemTo) {
			return true
		}

		// Match con activeMenuPath impostato dal composable
		if (normalizedActiveMenuPath && normalizedActiveMenuPath === normalizedItemTo) {
			return true
		}
	}

	// 3. Se nessun match, verifica tramite key (solo per item senza sottomenu)
	if (!hasItems && itemKey.value && activeMenuComposable.isKeyActive(itemKey.value)) {
		return true
	}

	return false
})

const itemClick = (event, item) => {
	console.log('itemClick event: ', event, ' item: ', item)

	// Verifica se è un link esterno (http/https)
	const isExternalLink = item.to && (item.to.startsWith('http://') || item.to.startsWith('https://'))

	if (isExternalLink) {
		// Per link esterni: reset del menu e lascia che il browser gestisca il link
		console.log('External link detected, resetting menu')
		activeMenuComposable.resetActiveMenu()
		// Non bloccare l'evento, lascia che il NuxtLink apra il link esterno
		return
	}

	// Per link interni: aggiorna il composable globale
	if (item.to) {
		activeMenuComposable.setActiveMenuByPath(item.to)
	}
	if (itemKey.value) {
		activeMenuComposable.setActiveMenuByKey(itemKey.value)
	}

	// Gestione categoria: imposta solo se presente, altrimenti reset
	if (item.idmenuRif) {
		clickListMenuCategory.value = item.idmenuRif
		activeMenuComposable.setActiveCategoryId(item.idmenuRif)
	} else {
		// Se non ha idmenuRif, reset della categoria
		activeMenuComposable.setActiveCategoryId(null)
	}

	if (item.disabled) {
		event.preventDefault()
		return
	}

	const { overlayMenuActive, staticMenuMobileActive } = layoutState

	if ((item.to || item.url) && (staticMenuMobileActive.value || overlayMenuActive.value)) {
		onMenuToggle()
	}

	if (item.command) {
		console.log('item.command: ', item.command)
		item.command({ originalEvent: event, item })
	}

	const foundItemKey = item.items ? (isExpandedMenu.value ? props.parentItemKey : itemKey) : itemKey.value
	setActiveMenuItem(foundItemKey)
}

const checkActiveRoute = (item) => {
	return route.path === item.to
}
</script>

<template>
  <li :class="{ 'layout-root-menuitem': root, 'active-menuitem': isExpandedMenu && !isThisItemActive }">
    <div v-if="root && item.visible !== false" class="layout-menuitem-root-text mt-3 mb-6">
      {{ item.label }}
    </div>
    <!--disattiviamo menu link a-->
   <!--  <a v-if="(!item.to || item.items) && item.visible !== false && false" :href="item.url" :class="item.class"
      :target="item.target" tabindex="0" class="my-2" @click="itemClick($event, item)">
      <i v-if="item.icon" :class="item.icon" class="layout-menuitem-icon" />
      <img v-if="item.img" :src="item.img" :alt="item.label" class="layout-menuitem-icon h-8  mr-4">
      <span class="layout-menuitem-text">{{ item.label }}</span>
      <i v-if="item.items" class="pi pi-fw1 pi-angle-down layout-submenu-toggler text-xl" />
    </a> -->
    <nuxt-link
      v-if="item.visible !== false"
      v-tooltip.right="tooltipText"
      class="align-items-center justify-content-start "
      :class="[item.class, { 'active-route': isThisItemActive }]"
      tabindex="0"
      :to="item.to"
      :target="item.target"
      @click="itemClick($event, item)"
    >
      <i v-if="item.icon" :class="item.icon" class="layout-menuitem-icon text-xl" />
      <Icon v-if="item.iconcustom" :name="item.iconcustom" class="layout-menuitem-icon text-xl h8" size="45" />
      <img v-if="item.imageBig" :src="item.imageBig" :alt="item.label" class="layout-menuitem-ic h-12 mr-4">
      <img v-if="item.img" :src="item.img" :alt="item.label" class="layout-menuitem-ico h-8 ">
      <span class="layout-menuitem-text">{{ item.label }}</span>
      <i v-if="item.items" class="pi pi-fw pi-angle-down layout-submenu-toggler" />
    </nuxt-link>
    <Transition v-if="item.items && item.visible !== false" name="layout-submenu">
      <ul v-show="root ? true : isExpandedMenu" class="layout-submenu">
        <app-menu-item v-for="(child, i) in item.items" :key="child" :index="i" :item="child" :parent-item-key="itemKey || undefined"
          :root="false" :compact="compact" />
      </ul>
    </Transition>
  </li>
</template>

<style lang="scss" scoped>
</style>
